import React, { useState } from 'react'
import PageHeader from '../components/PageHeader'

export default function Contact() {
  const [formData, setFormData] = useState({
    navn: '',
    telefonnummer: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot mod spam bots
  })

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus({ submitting: true, success: false, error: null })

    // Validering
    if (!formData.navn.trim() || !formData.email.trim() || !formData.beskrivelse.trim()) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst de påkrævede felter (navn, email og henvendelse).'
      })
      return
    }

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefonnummer: formData.telefonnummer,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url // Honeypot
        })
      })

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null })
        setFormData({
          navn: '',
          telefonnummer: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        })
      } else {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.')
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en fejl ved kontaktformularen. Ring gerne direkte til os på 25 77 86 46.'
      })
    }
  }

  return (
    <div>
      <PageHeader
        badge="Kom i kontakt"
        title="Kontakt Unges Fremtid"
        subtitle="Udfyld formularen herunder med din henvendelse, så vender vi tilbage hurtigst muligt. Du kan også ringe til os på hverdage mellem kl. 9.00 og 15.00."
        breadcrumb={[{ label: 'Kontakt' }]}
      />

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Foreningsoplysninger */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-[#f39223]">
                  Direkte kontakt
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0c304d]">
                  Vi er her for at hjælpe
                </h2>
                <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
                  Du kan altid ringe til vores hovednummer på 25 77 86 46 på hverdage mellem kl. 9.00 og 15.00. Du er også altid velkommen til at sende os en mail eller udfylde formularen.
                </p>
              </div>

              {/* Foreningsoplysninger kort */}
              <div className="bg-[#faf9f6] rounded-3xl p-6 sm:p-8 border border-neutral-200/90 shadow-2xs space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#f39223]">
                    Foreningsoplysninger
                  </span>
                  <h3 className="text-2xl font-extrabold text-[#0c304d] mt-1">
                    Unges Fremtid
                  </h3>
                </div>

                <div className="space-y-4 text-sm text-neutral-700">
                  {/* Adresse */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">Adresse</p>
                      <p className="text-neutral-700 text-base">Koglevej 36, 8960 Randers SØ</p>
                    </div>
                  </div>

                  {/* Telefon (klik for opkald) */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">Telefon (klik for opkald)</p>
                      <a
                        href="tel:25778646"
                        className="text-[#0c304d] hover:text-[#f39223] font-extrabold text-lg transition-colors whitespace-nowrap block"
                      >
                        25 77 86 46
                      </a>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Hverdage kl. 9.00 - 15.00
                      </p>
                    </div>
                  </div>

                  {/* E-mail (klik for mail) */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">E-mail (klik for mail)</p>
                      <a
                        href="mailto:ungesfremtid@outlook.dk"
                        className="text-[#0c304d] hover:text-[#f39223] font-bold text-base transition-colors break-all block"
                      >
                        ungesfremtid@outlook.dk
                      </a>
                    </div>
                  </div>

                  {/* CVR-nr */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-id-card" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">CVR-nr:</p>
                      <p className="text-neutral-800 font-semibold text-base">46681606</p>
                    </div>
                  </div>

                  {/* Bankoplysninger */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-building-columns" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">Bankoplysninger</p>
                      <p className="text-neutral-800 font-semibold text-base">Reg. nr. 6124</p>
                      <p className="text-neutral-800 font-semibold text-base">Konto 0024431401</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-neutral-200 text-xs text-neutral-600 space-y-1">
                  <p className="flex items-center gap-2">
                    <i className="fa-solid fa-clock text-[#f39223]" aria-hidden="true" />
                    <span className="font-medium text-neutral-800">Telefontid: Hverdage kl. 9.00 – 15.00</span>
                  </p>
                  <p className="text-neutral-500">
                    Aktiviteter udføres i hele Danmark med udgangspunkt i Randers.
                  </p>
                </div>
              </div>

              {/* Tryghedsgaranti */}
              <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200/80 flex items-start gap-3.5">
                <i className="fa-solid fa-shield-heart text-[#f39223] text-lg mt-0.5" aria-hidden="true" />
                <p className="text-xs sm:text-sm text-neutral-700 leading-relaxed">
                  <strong className="font-bold text-[#0c304d]">Fortrolighed:</strong> Alle henvendelser behandles med største diskretion, respekt og tavshedspligt.
                </p>
              </div>
            </div>

            {/* Kontaktformular */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl p-6 sm:p-10 border border-neutral-200 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-bold text-[#0c304d] mb-2">
                  Send os en besked
                </h3>
                <p className="text-sm text-neutral-600 mb-2">
                  Udfyld formularen herunder med din henvendelse, så vender vi tilbage hurtigst muligt.
                </p>
                <p className="text-xs text-neutral-500 mb-8">
                  Du kan også ringe til vores hovednummer på <a href="tel:25778646" className="font-bold text-[#0c304d] underline">25 77 86 46</a> på hverdage mellem kl. 9.00 og 15.00, eller skrive direkte til <a href="mailto:ungesfremtid@outlook.dk" className="underline font-semibold">ungesfremtid@outlook.dk</a>.
                </p>

                {status.success ? (
                  <div className="p-8 rounded-2xl bg-green-50 border border-green-200 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-3xl mx-auto">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h4 className="text-xl font-bold text-green-900">
                      Tak for din henvendelse!
                    </h4>
                    <p className="text-neutral-700 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
                      Vi har modtaget din besked og vender tilbage hurtigst muligt. Hvis det haster, er du altid velkommen til at ringe på <span className="whitespace-nowrap font-bold text-neutral-900">25 77 86 46</span>.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus({ submitting: false, success: false, error: null })}
                      className="mt-4 px-6 py-2.5 bg-[#0c304d] hover:bg-[#16466e] text-white rounded-full text-sm font-semibold transition-colors cursor-pointer"
                    >
                      Send endnu en besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                    {/* ANTISPAM HONEYPOT */}
                    <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="contact_website_url">Website URL (leave empty)</label>
                      <input
                        type="text"
                        id="contact_website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label htmlFor="navn" className="block text-sm font-semibold text-neutral-800 mb-1.5">
                        Dit navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="navn"
                        name="navn"
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="F.eks. Anders Hansen"
                        required
                        className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="telefonnummer" className="block text-sm font-semibold text-neutral-800 mb-1.5">
                          Telefonnummer
                        </label>
                        <input
                          type="tel"
                          id="telefonnummer"
                          name="telefonnummer"
                          value={formData.telefonnummer}
                          onChange={handleChange}
                          placeholder="F.eks. 12 34 56 78"
                          className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-neutral-800 mb-1.5">
                          E-mailadresse <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="din@email.dk"
                          required
                          className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="beskrivelse" className="block text-sm font-semibold text-neutral-800 mb-1.5">
                        Din henvendelse / besked <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="beskrivelse"
                        name="beskrivelse"
                        rows={5}
                        value={formData.beskrivelse}
                        onChange={handleChange}
                        placeholder="Hvad kan vi hjælpe med? Skriv gerne lidt om din situation..."
                        required
                        className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50 resize-y"
                      />
                    </div>

                    {status.error && (
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
                        {status.error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#f39223] hover:bg-[#e08112] text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-all duration-200 shadow-sm hover:shadow disabled:opacity-50 cursor-pointer text-center"
                    >
                      {status.submitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          <span>Sender henvendelse...</span>
                        </>
                      ) : (
                        <>
                          <span>Send besked</span>
                          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
